import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { observer } from 'mobx-react-lite';
import type { ActivityPlan } from '@linktivity/ars-neo';
import { Info, Right } from '@linktivity/link-icons';
import { PlanPrice, TimeSlotChips } from '@link/share/booking';
import Img from '@link/share/components/Img';
import { useDateFormat } from '@link/share/hooks/useDateFormat';
import { formatTime, isStartTimeExpired } from '@link/share/utils';
import { useBookingStore } from '../context';
import { PlanPriceTable } from './PlanPriceTable';
import styles from './planPicker.module.css';

/**
 * The column layout's plan step: the plans side by side over the active
 * plan's picture, then its start times.
 *
 * Pictures, names and prices are the product's and render on the server. Sold
 * out, the auto-switch notice, the price table and the start times all depend
 * on the chosen date's stock, so they appear once the calendar has loaded.
 */
export const PlanPicker = observer(function PlanPicker() {
  const store = useBookingStore();
  const { t } = useTranslation();
  const { formatMonthDayWeekday } = useDateFormat();
  const plans = store.collapsiblePlans;
  const activePlan =
    plans.find(plan => plan.activityPlan.id === store.activePlanId) ?? plans[0];
  const activePlanId = activePlan?.activityPlan.id ?? '';
  // One calendar serves every plan, so each card is judged against the
  // active plan's date. Plans booked without a date (lottery, open date)
  // cannot sell out on one.
  const date = activePlan?.showsCalendarDate
    ? (activePlan.defaultTargetDate ?? '')
    : '';
  const isSoldOut = (planId: string, showsCalendarDate: boolean) =>
    !!date &&
    showsCalendarDate &&
    !!store.isPlanBookableOnDate &&
    !store.isPlanBookableOnDate(planId, date);

  // The active plan's start times on the date, minus those already past.
  const timeSlots =
    activePlan?.showsCalendarDate && !store.calendar.isLoading
      ? activePlan.startTimes
          .filter(slot => !isStartTimeExpired(date, slot.time))
          .map(slot => ({
            id: slot.id,
            label: slot.title || formatTime(slot.time),
            stock: slot.status
          }))
      : [];

  const switchedFrom = plans.find(
    plan =>
      plan.activityPlan.id === store.autoSwitchedFromPlanId &&
      isSoldOut(plan.activityPlan.id ?? '', plan.showsCalendarDate)
  );

  // One block: the step lays its children out with a gap, and the tabs must
  // overlap the gallery.
  return (
    <div>
      {/* Every plan's image is in the markup, stacked, and only the active
          one is visible, so switching plans cross-fades instead of loading. */}
      <div className={styles.gallery}>
        {plans
          .filter(({ activityPlan }) => activityPlan.imageUrls?.[0])
          .map(({ activityPlan }) => (
            <Img
              key={activityPlan.id ?? ''}
              src={activityPlan.imageUrls?.[0]}
              alt={activityPlan.title ?? ''}
              className={cls(
                styles.image,
                activityPlan.id === activePlanId && styles.active
              )}
            />
          ))}
        <PlanPriceTable date={activePlan?.defaultTargetDate ?? ''} />
      </div>
      <div className={styles.tabs}>
        {plans.map(({ activityPlan, showsCalendarDate }) => (
          <PlanTab
            key={activityPlan.id ?? ''}
            plan={activityPlan}
            selected={activityPlan.id === activePlanId}
            soldOut={isSoldOut(activityPlan.id ?? '', showsCalendarDate)}
          />
        ))}
      </div>
      {switchedFrom && activePlan && (
        // Why the plan being booked is not the one the visitor picked: it
        // sold out on the chosen date, and the page moved to one in stock.
        <p className={styles.switchNotice}>
          <Info className={styles.switchNoticeIcon} />
          <span className={styles.switchNoticeText}>
            {t('share.planCard.planAutoSwitchNotice', {
              date: formatMonthDayWeekday(date),
              soldOutPlan: switchedFrom.activityPlan.title ?? '',
              selectedPlan: activePlan.activityPlan.title ?? ''
            })}
          </span>
        </p>
      )}
      {activePlan && timeSlots.length > 0 && (
        <TimeSlotChips
          slots={timeSlots}
          value={activePlan.selectedTimeSlot}
          onSelect={timeId => store.selectTime(activePlanId, timeId)}
        />
      )}
    </div>
  );
});

interface TabProps {
  plan: ActivityPlan;
  selected: boolean;
  /** No stock on the chosen date. */
  soldOut: boolean;
}

/**
 * A plan in the row of plans: picking it makes it the plan being booked.
 * Sold out on the chosen date, it greys out and stops responding, but its
 * details stay readable.
 */
const PlanTab = observer(function PlanTab({
  plan,
  selected,
  soldOut
}: TabProps) {
  const store = useBookingStore();
  const { t } = useTranslation();
  const planId = plan.id ?? '';
  const price = store.startingPrices.get(planId);
  return (
    <div
      className={cls(
        styles.tab,
        selected && !soldOut && styles.selected,
        soldOut && styles.soldOut
      )}
    >
      <button
        type="button"
        className={styles.pick}
        disabled={soldOut}
        aria-pressed={selected && !soldOut}
        onClick={() => store.expandPlan(planId, true)}
      >
        <span className={styles.tabTitle}>{plan.title}</span>
        <div className={styles.priceRow}>
          {soldOut ? (
            <span className={styles.soldOutLabel}>
              {t('share.planCard.soldOut')}
            </span>
          ) : (
            price && (
              <PlanPrice
                label={price.label}
                amount={price.amount}
                currency={store.currency}
              />
            )
          )}
        </div>
      </button>
      <button
        type="button"
        className={styles.detailsButton}
        onClick={() => store.showPlanDetail(plan)}
      >
        {t('share.planCard.facilityDetails')}
        <Right className={styles.detailsIcon} />
      </button>
    </div>
  );
});
